import React, { useState } from 'react';
import { transactionsAPI, toolsAPI, employeesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const inp = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent";
const statusClasses = {
  available: 'bg-emerald-100 text-emerald-700',
  checked_out: 'bg-blue-100 text-blue-700',
  overdue: 'bg-red-100 text-red-700',
  maintenance: 'bg-amber-100 text-amber-700',
  calibration: 'bg-orange-100 text-orange-700',
  retired: 'bg-slate-100 text-slate-600',
  excellent: 'bg-emerald-100 text-emerald-700',
  good: 'bg-green-100 text-green-700',
  fair: 'bg-yellow-100 text-yellow-700',
  poor: 'bg-red-100 text-red-700',
};
const Badge = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${statusClasses[status] || 'bg-slate-100 text-slate-600'}`}>{status}</span>
);

const DetailRow = ({ label, children }) => (
  <div className="flex justify-between items-center py-2.5 border-b border-slate-100 last:border-0">
    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
    <span className="text-sm text-slate-900 font-medium">{children}</span>
  </div>
);

const ToolCheckout = () => {
  const { isTechnician } = useAuth();
  const { toast } = useToast();
  const [mode, setMode] = useState(isTechnician ? 'return' : 'checkout');
  const [serialNumber, setSerialNumber] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [tool, setTool] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [expectedReturnDate, setExpectedReturnDate] = useState('');
  const [purpose, setPurpose] = useState('');
  const [returnCondition, setReturnCondition] = useState('good');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleScanTool = async () => {
    if (!serialNumber.trim()) { toast('Please enter a serial number', 'warning'); return; }
    setLoading(true); setTool(null);
    try {
      const response = await toolsAPI.getByBarcode(serialNumber);
      const toolData = response.data.tool;
      if (mode === 'checkout' && toolData.status !== 'available') {
        toast(`Tool is not available. Current status: ${toolData.status}`, 'error');
      } else if (mode === 'return' && toolData.status !== 'checked_out') {
        toast(`Tool is not checked out. Current status: ${toolData.status}`, 'error');
      } else {
        setTool(toolData);
      }
    } catch (err) {
      const e = err.response?.data?.error;
      toast(typeof e === 'string' ? e : e?.message || 'Tool not found with this serial number', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFindEmployee = async () => {
    if (!employeeId.trim()) { toast('Please enter an employee ID', 'warning'); return; }
    setLoading(true); setEmployee(null);
    try {
      const response = await employeesAPI.getAll({ employeeCode: employeeId });
      const employees = response.data.employees;
      if (employees && employees.length > 0) {
        setEmployee(employees[0]);
      } else {
        toast('Employee not found', 'error');
      }
    } catch {
      toast('Employee not found', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (!tool || !employee) { toast('Please scan tool and find employee first', 'warning'); return; }
    if (!expectedReturnDate) { toast('Please select expected return date', 'warning'); return; }
    setLoading(true);
    try {
      await transactionsAPI.checkout({ toolId: tool._id, employeeId: employee._id, expectedReturnDate, purpose: purpose || 'General use', notes });
      toast('Tool checked out successfully!', 'success');
      resetForm();
    } catch (err) {
      const e = err.response?.data?.error;
      toast(typeof e === 'string' ? e : e?.message || 'Checkout failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReturn = async () => {
    if (!tool) { toast('Please scan tool first', 'warning'); return; }
    setLoading(true);
    try {
      await transactionsAPI.return({ toolId: tool._id, returnCondition, notes });
      toast('Tool returned successfully!', 'success');
      resetForm();
    } catch (err) {
      const e = err.response?.data?.error;
      toast(typeof e === 'string' ? e : e?.message || 'Return failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSerialNumber(''); setEmployeeId(''); setTool(null); setEmployee(null);
    setExpectedReturnDate(''); setPurpose(''); setReturnCondition('good'); setNotes('');
  };

  return (
    <div className="space-y-5">
      {/* Mode Selector */}
      <div className="flex gap-3">
        {!isTechnician && (
          <button
            onClick={() => { setMode('checkout'); resetForm(); }}
            className={`flex-1 flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl font-semibold text-sm border-2 transition-all
              ${mode === 'checkout' ? 'bg-brand-primary border-brand-primary text-white shadow-lg shadow-red-200' : 'bg-white border-slate-200 text-slate-600 hover:border-brand-primary/40 hover:text-brand-primary'}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
            Issues
          </button>
        )}
        <button
          onClick={() => { setMode('return'); resetForm(); }}
          className={`flex-1 flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl font-semibold text-sm border-2 transition-all
            ${mode === 'return' ? 'bg-emerald-600 border-emerald-600 text-white shadow-lg shadow-emerald-200' : 'bg-white border-slate-200 text-slate-600 hover:border-emerald-300 hover:text-emerald-600'}`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
          </svg>
         Collect
        </button>
      </div>
      {isTechnician && (
        <p className="text-xs text-slate-500 flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          As a Technician, you can only return tools. Contact a Supervisor or Admin to check out tools.
        </p>
      )}

      {/* Scan Cards */}
      <div className={`grid grid-cols-1 ${mode === 'checkout' ? 'lg:grid-cols-2' : ''} gap-5`}>
        {/* Tool Scan */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
            <div className="w-8 h-8 bg-brand-primary-bg rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-brand-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h3 className="font-semibold text-slate-900">Scan Tool</h3>
          </div>
          <div className="p-5 space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Serial Number / Barcode</label>
              <div className="flex gap-2">
                <input type="text" className={inp} placeholder="Enter or scan serial number"
                  value={serialNumber} onChange={e => setSerialNumber(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && handleScanTool()} />
                <button onClick={handleScanTool} disabled={loading}
                  className="px-4 py-2 bg-brand-primary hover:bg-brand-primary-dark text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 flex-shrink-0">
                  {loading ? '...' : 'Scan'}
                </button>
              </div>
            </div>
            {tool && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-3 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Tool Found
                </p>
                <DetailRow label="Tool Name">{tool.tool_name}</DetailRow>
                <DetailRow label="Serial #"><code className="text-xs bg-white px-2 py-0.5 rounded border border-slate-200">{tool.serial_number}</code></DetailRow>
                <DetailRow label="Category">{tool.category}</DetailRow>
                <DetailRow label="Status"><Badge status={tool.status} /></DetailRow>
                <DetailRow label="Condition">{tool.condition}</DetailRow>
              </div>
            )}
          </div>
        </div>

        {/* Employee (checkout only) */}
        {mode === 'checkout' && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="font-semibold text-slate-900">Employee Information</h3>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Employee ID / Code</label>
                <div className="flex gap-2">
                  <input type="text" className={inp} placeholder="Enter employee ID"
                    value={employeeId} onChange={e => setEmployeeId(e.target.value)}
                    onKeyPress={e => e.key === 'Enter' && handleFindEmployee()} />
                  <button onClick={handleFindEmployee} disabled={loading}
                    className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 flex-shrink-0">
                    {loading ? '...' : 'Find'}
                  </button>
                </div>
              </div>
              {employee && (
                <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
                  <p className="text-xs font-semibold text-violet-600 uppercase tracking-wider mb-3 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Employee Found
                  </p>
                  <DetailRow label="Name">{employee.first_name} {employee.last_name}</DetailRow>
                  <DetailRow label="Code"><code className="text-xs bg-white px-2 py-0.5 rounded border border-slate-200">{employee.employee_code}</code></DetailRow>
                  <DetailRow label="Department">{employee.department}</DetailRow>
                  <DetailRow label="Role">{employee.role}</DetailRow>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Transaction Details */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h3 className="font-semibold text-slate-900">{mode === 'checkout' ? 'Checkout' : 'Return'} Details</h3>
        </div>
        <div className="p-5">
          {mode === 'checkout' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Expected Return Date *</label>
                <input type="date" className={inp} value={expectedReturnDate}
                  onChange={e => setExpectedReturnDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Purpose</label>
                <input type="text" className={inp} placeholder="e.g., Maintenance work, Project X"
                  value={purpose} onChange={e => setPurpose(e.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Notes</label>
                <textarea className={inp} rows="3" placeholder="Any additional notes..."
                  value={notes} onChange={e => setNotes(e.target.value)} />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Return Condition *</label>
                <select className={inp} value={returnCondition} onChange={e => setReturnCondition(e.target.value)}>
                  <option value="excellent">Excellent</option>
                  <option value="good">Good</option>
                  <option value="fair">Fair</option>
                  <option value="poor">Poor (Needs Maintenance)</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Notes</label>
                <textarea className={inp} rows="3" placeholder="Any issues or observations..."
                  value={notes} onChange={e => setNotes(e.target.value)} />
              </div>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <button onClick={resetForm} className="px-4 py-2 border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-colors">
              Clear Form
            </button>
            {mode === 'checkout' ? (
              <button onClick={handleCheckout} disabled={!tool || !employee || loading}
                className="px-5 py-2 bg-brand-primary hover:bg-brand-primary-dark text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? 'Processing...' : 'Complete Checkout'}
              </button>
            ) : (
              <button onClick={handleReturn} disabled={!tool || loading}
                className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? 'Processing...' : 'Complete Return'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToolCheckout;
